/**
 * Isolamento por operador e regime de isolamento — puro, sem React, para o
 * check de contrato. Contrato: README do Athena §4.9.
 *
 * Duas leituras que a tela NÃO pode errar:
 *
 *  - `recommended` num alerta é um CONVITE ao operador, nunca um estado da
 *    máquina: o agente não isolou.
 *  - `pending`/`delivered` de um `isolate_host` ou de um `set_isolation_policy`
 *    não é "isolada" nem "regime trocado". Só `executed`/`skipped` confirmam.
 */
import type { Tone } from '../../components/primitives/Metric.tsx'
import type { IsolationPolicyMode, MachineIsolationPolicy } from '../api/types.ts'
import type { AlertResponseAction } from '../sse/types.ts'
import { failureText } from './semantics.ts'

export const ISOLATE_KIND = 'isolate_host'
export const SET_ISOLATION_POLICY_KIND = 'set_isolation_policy'

/** O que isolar faz com a máquina. Fica no diálogo, antes do envio. */
export const ISOLATE_CONSEQUENCE =
  'A máquina perde toda a rede, exceto o canal com o Athena, até um desbloqueio.'

/** O aviso ao escolher Automático. */
export const AUTOMATIC_WARNING =
  'O agente vai isolar a máquina sozinho em alertas críticos, sem ninguém olhar.'

/** O padrão da empresa vale só para agentes NOVOS. */
export const COMPANY_DEFAULT_SCOPE =
  'Vale só para os agentes que se registrarem depois: as máquinas que já existem não mudam.'

/** O alerta traz `isolate_host` com `recommended`: o agente pede uma decisão. */
export function hasRecommendedIsolation(response: readonly AlertResponseAction[]): boolean {
  return response.some((item) => item.action === ISOLATE_KIND && item.status === 'recommended')
}

/** O que a tela pode AFIRMAR sobre a rede da máquina depois de um `isolate_host`. */
export function isolateOutcome(state: string): { tone: Tone; headline: string; isolated: boolean } {
  switch (state) {
    case 'pending':
      return {
        tone: 'accent',
        headline: 'Isolamento pedido — na fila, aguardando o agente buscar. A máquina ainda NÃO está isolada.',
        isolated: false,
      }
    case 'delivered':
      return {
        tone: 'warn',
        headline: 'Isolamento pedido — entregue ao agente, sem confirmação. A máquina ainda NÃO está isolada.',
        isolated: false,
      }
    case 'executed':
      return { tone: 'ok', headline: 'Máquina isolada: o agente confirmou o isolamento.', isolated: true }
    case 'skipped':
      return { tone: 'ok', headline: 'Máquina isolada: o agente confirmou que ela já estava isolada.', isolated: true }
    case 'failed':
      return { tone: 'crit', headline: 'O sistema recusou o isolamento. A máquina segue na rede.', isolated: false }
    case 'expired':
      return {
        tone: 'warn',
        headline: 'Venceu: 15 min sem o agente buscar o comando. A máquina segue na rede.',
        isolated: false,
      }
    case 'cancelled':
      return { tone: 'neutral', headline: 'Isolamento cancelado. A máquina segue na rede.', isolated: false }
    default:
      return {
        tone: 'neutral',
        headline: `Estado "${state}" desconhecido.`,
        isolated: false,
      }
  }
}

/** O que a tela pode afirmar sobre o regime depois de um `set_isolation_policy`. */
export function policyOutcome(state: string): { tone: Tone; headline: string; changed: boolean } {
  switch (state) {
    case 'pending':
      return { tone: 'accent', headline: 'Pedido na fila, aguardando o agente buscar. O regime ainda não mudou.', changed: false }
    case 'delivered':
      return { tone: 'warn', headline: 'Entregue ao agente, sem confirmação. O regime ainda não mudou.', changed: false }
    case 'executed':
      return { tone: 'ok', headline: 'Regime trocado: o agente confirmou.', changed: true }
    case 'skipped':
      return { tone: 'ok', headline: 'O agente confirmou: o regime já era esse.', changed: true }
    case 'failed':
      return { tone: 'crit', headline: 'O agente não gravou o regime. Segue o anterior.', changed: false }
    case 'expired':
      return { tone: 'warn', headline: 'Venceu sem o agente buscar (7 dias). Segue o anterior.', changed: false }
    case 'cancelled':
      return { tone: 'neutral', headline: 'Pedido cancelado (substituído por outro). Segue o anterior.', changed: false }
    default:
      return { tone: 'neutral', headline: `Estado "${state}" desconhecido.`, changed: false }
  }
}

/** O regime EM VIGOR, com "(padrão)" quando ninguém definiu. Nunca o pedido. */
export function effectivePolicyLabel(policy: Pick<MachineIsolationPolicy, 'effective' | 'is_default'>): string {
  return policy.is_default ? `${policy.effective.label} (padrão)` : policy.effective.label
}

const DIVERGENCE: Record<string, string> = {
  pending: 'Aguardando o agente buscar',
  delivered: 'Entregue, aguardando confirmação',
  failed: 'O agente não aplicou',
  expired: 'Venceu sem o agente buscar (7 dias)',
  cancelled: 'Pedido cancelado',
  agent_changed: 'O agente foi reinstalado; o regime pedido não vale para a instalação atual',
}

/** Por que o pedido não está confirmado. `failed` leva o texto do agente. */
export function divergenceText(
  policy: Pick<MachineIsolationPolicy, 'divergence' | 'desired_command'>,
): string | null {
  const divergence = policy.divergence
  if (divergence === null) return null
  const text = DIVERGENCE[divergence] ?? `Pedido não confirmado (${divergence})`
  if (divergence !== 'failed' || policy.desired_command === null) return text
  const detail = failureText(policy.desired_command)
  return detail === null ? text : `${text}: ${detail}`
}

/** Divergências em que o pedido não vai mais andar sozinho: ofereça salvar de novo. */
export function divergenceOffersResave(divergence: string | null): boolean {
  return divergence === 'expired' || divergence === 'agent_changed' || divergence === 'failed'
}

export const POLICY_MODES: readonly IsolationPolicyMode[] = ['automatic', 'recommend', 'disabled']

export function isPolicyMode(value: string): value is IsolationPolicyMode {
  return (POLICY_MODES as readonly string[]).includes(value)
}

/** O `mode` de `params` (`'{"mode":"…"}'`). `null` quando falta ou não é JSON — nunca lança. */
export function policyModeOf(command: { params?: string | null }): string | null {
  if (command.params == null || command.params === '') return null
  try {
    const parsed: unknown = JSON.parse(command.params)
    if (parsed === null || typeof parsed !== 'object') return null
    const mode = (parsed as Record<string, unknown>).mode
    return typeof mode === 'string' && mode !== '' ? mode : null
  } catch {
    return null
  }
}
